import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authenticatedApiClient } from './axios'
import type { Gathering, CheckInParams, UpdateCheckInParams, RSVP, Comment } from '@cobu/types'

// Local input type to avoid app-specific schema coupling
export interface GroupEventFormValuesInput {
  title: string
  locationString: string
  description: string
  maxEventAttendees: string | number
  maxGuests: string | number
  whatToBring?: string
  startTime?: Date | string
  imageFile?: File | null
  removeImage?: boolean
}

/**
 * Events service for handling event/gathering-related operations
 */
export class EventsService {
  /**
   * Create a new group event
   */
  async createGroupEvent(groupId: string, eventData: GroupEventFormValuesInput, creator: string): Promise<any> {
    const formData = new FormData()

    // Add event data to form
    formData.append('group', groupId)
    formData.append('title', eventData.title.trim())
    formData.append('locationString', eventData.locationString.trim())
    formData.append('description', eventData.description.trim())
    if (eventData.maxEventAttendees !== undefined && eventData.maxEventAttendees !== null && String(eventData.maxEventAttendees).trim() !== '') {
      formData.append('maxEventAttendees', String(eventData.maxEventAttendees).trim())
    }
    if (eventData.maxGuests !== undefined && eventData.maxGuests !== null && String(eventData.maxGuests).trim() !== '') {
      formData.append('maxGuests', String(eventData.maxGuests).trim())
    }
    formData.append('isRSVP', 'true')
    formData.append('commitType', 'e8a642e9-7fe0-4f5a-9279-4cec3db10dd2')
    formData.append('recurringType', 'c8b2ee7a-a67e-406f-b79e-6f2a08b10421')
    formData.append('creator', creator)

    if (eventData.whatToBring && eventData.whatToBring.trim()) {
      formData.append('whatToBring', eventData.whatToBring.trim())
    }

    // Format the start time for the API
    const formattedStartTime = eventData.startTime
      ? (eventData.startTime instanceof Date ? eventData.startTime.toISOString() : String(eventData.startTime))
      : ''
    formData.append('startTime', formattedStartTime)

    // Add image file if provided (web File)
    if (eventData.imageFile instanceof File) {
      formData.append('image', eventData.imageFile, eventData.imageFile.name || 'event-image.jpg')
    }

    const response = await authenticatedApiClient.post<any>('/groupevent', formData)

    return response
  }

  /**
   * Update an existing group event
   */
  async updateGroupEvent(eventId: string, groupId: string, eventData: GroupEventFormValuesInput): Promise<any> {
    const formData = new FormData()

    // Add event data to form
    formData.append('group', groupId)
    formData.append('title', eventData.title.trim())
    formData.append('locationString', eventData.locationString.trim())
    formData.append('description', eventData.description.trim())
    if (eventData.maxEventAttendees !== undefined && eventData.maxEventAttendees !== null && String(eventData.maxEventAttendees).trim() !== '') {
      formData.append('maxEventAttendees', String(eventData.maxEventAttendees).trim())
    }
    if (eventData.maxGuests !== undefined && eventData.maxGuests !== null && String(eventData.maxGuests).trim() !== '') {
      formData.append('maxGuests', String(eventData.maxGuests).trim())
    }
    formData.append('isRSVP', 'true')
    formData.append('commitType', 'e8a642e9-7fe0-4f5a-9279-4cec3db10dd2')
    formData.append('recurringType', 'c8b2ee7a-a67e-406f-b79e-6f2a08b10421')

    if (eventData.whatToBring && eventData.whatToBring.trim()) {
      formData.append('whatToBring', eventData.whatToBring.trim())
    }

    // Format the start time for the API
    const formattedStartTime = eventData.startTime
      ? (eventData.startTime instanceof Date ? eventData.startTime.toISOString() : String(eventData.startTime))
      : ''
    formData.append('startTime', formattedStartTime)

    // Add image file if provided (web File)
    if (eventData.imageFile instanceof File) {
      formData.append('image', eventData.imageFile, eventData.imageFile.name || 'event-image.jpg')
    }
    // Explicitly remove image when requested
    if (!eventData.imageFile && eventData.removeImage) {
      formData.append('removeImage', 'true')
    }

    const response = await authenticatedApiClient.patch<any>(`/groupevent/${eventId}`, formData)
    return response
  }

  /**
   * Get upcoming gatherings for a building
   */
  async getGatherings(buildingUuid: string): Promise<Gathering[]> {
    const config: any = {
      params: {
        building: [buildingUuid],
        sort: 'scheduledStartTime ASC',
        view: 'UPCOMING',
      },
    }

    const response = await authenticatedApiClient.get<Gathering[]>('/groupevent', config)
    return response
  }

  /**
   * Get past gatherings for a building
   */
  async getPastGatherings(buildingUuid: string): Promise<Gathering[]> {
    const config: any = {
      params: {
        building: [buildingUuid],
        sort: 'scheduledStartTime DESC',
        cancelled: false,
        view: 'PAST',
      },
    }

    const response = await authenticatedApiClient.get<Gathering[]>('/groupevent', config)
    return response
  }

  /**
   * Check in to a gathering
   */
  async checkIn(params: CheckInParams): Promise<void> {
    await authenticatedApiClient.post(`/post/${params.gatheringId}/checkin`, {
      type: params.type || 'rsvp',
      totalAttendees: params.totalAttendees || 1,
    })
  }

  /**
   * Update check-in status for a gathering
   */
  async updateCheckIn(params: UpdateCheckInParams): Promise<void> {
    await authenticatedApiClient.patch(`/post/${params.gatheringId}/checkin/${params.checkInId}`, {
      totalAttendees: params.totalAttendees || 1,
    })
  }

  /**
   * Remove check-in from a gathering
   */
  async unCheckIn(gatheringId: string, checkInId: string): Promise<void> {
    await authenticatedApiClient.delete(`/post/${gatheringId}/checkin/${checkInId}`)
  }

  /**
   * Get a single gathering by ID
   */
  async getGathering(gatheringId: string, eventDate?: string): Promise<Gathering> {
    const config: any = {}
    // Only include eventDate if it's explicitly provided and not empty
    // Passing eventDate for non-recurring events causes "Group event not found" error
    if (eventDate && eventDate.trim() !== '') {
      config.params = { eventDate: eventDate.trim() }
    }
    const response = await authenticatedApiClient.get<Gathering>(`/v2/gatherings/${gatheringId}`, config)
    return response
  }

  /**
   * Get gathering RSVPs
   */
  async getGatheringRSVPs(gatheringId: string, eventDate: string): Promise<RSVP[]> {
    const config: any = {
      params: {
        interestedDate: eventDate,
        client: 'MOBILE',
      },
    }

    const response = await authenticatedApiClient.get<RSVP[]>(`/groupevent/${gatheringId}/rsvp`, config)
    return response
  }

  /**
   * Create gathering RSVP
   */
  async createGatheringRSVP(gatheringId: string, userId: string, totalAttendees: number, eventDate: string): Promise<any> {
    const response = await authenticatedApiClient.post('/groupeventrsvp', {
      groupEvent: gatheringId,
      interestedDate: eventDate,
      userForRsvp: userId,
      totalAttendees,
    })
    return response
  }

  /**
   * Update gathering RSVP
   */
  async updateGatheringRSVP(rsvpId: string, totalAttendees: number, eventDate: string): Promise<any> {
    const response = await authenticatedApiClient.patch(`/groupeventrsvp/${rsvpId}`, {
      totalAttendees,
      interestedDate: eventDate,
    })
    return response
  }

  /**
   * Delete gathering RSVP
   */
  async deleteGatheringRSVP(rsvpId: string): Promise<any> {
    const response = await authenticatedApiClient.delete(`/groupeventrsvp/${rsvpId}`)
    return response
  }

  /**
   * Get gathering comments
   */
  async getGatheringComments(gatheringId: string, eventDate: string): Promise<Comment[]> {
    const config: any = {
      params: {
        groupEvent: gatheringId,
        interestedDate: eventDate,
        sort: 'createdAt DESC',
      },
      headers: {
        client: 'MOBILE',
      },
    }

    const response = await authenticatedApiClient.get<Comment[]>('/groupeventcomment', config)
    return response
  }

  /**
   * Create gathering comment
   */
  async createGatheringComment(gatheringId: string, eventDate: string, commentData: { contents?: string; parent?: string; editingReport?: string }): Promise<any> {
    const formData = new FormData()
    formData.append('groupEvent', gatheringId)
    formData.append('interestedDate', eventDate)

    if (commentData.contents) {
      formData.append('contents', commentData.contents.trim())
    }
    if (commentData.parent) {
      formData.append('parent', commentData.parent)
    }
    if (commentData.editingReport) {
      formData.append('editingReport', commentData.editingReport)
    }

    const response = await authenticatedApiClient.post('/v2/groupeventcomment', formData)
    return response
  }

  /**
   * Delete gathering comment (soft delete)
   */
  async deleteGatheringComment(commentId: string): Promise<any> {
    const response = await authenticatedApiClient.patch(`/groupeventcomment/${commentId}`, {
      removed: true,
      removedBy: 'USER',
      hidden: false,
    })
    return response
  }

  /**
   * Cancel a gathering
   */
  async cancelGathering(gatheringId: string): Promise<any> {
    const response = await authenticatedApiClient.patch(`/groupevent/${gatheringId}`, {
      cancelled: true,
    })
    return response
  }

  /**
   * Get group members
   */
  async getGroupMembers(groupId: string): Promise<any[]> {
    const config: any = {
      params: {
        group: groupId,
        active: true,
      },
    }

    const response = await authenticatedApiClient.get<any[]>('/groupmember', config)
    return response
  }
}

const eventsService = new EventsService()
export default eventsService

// Query and mutation keys
export const EVENTS_KEYS = {
  gatherings: (buildingUuid: string) => ['gatherings', buildingUuid] as const,
  pastGatherings: (buildingUuid: string) => ['pastGatherings', buildingUuid] as const,
  gathering: (gatheringId: string, eventDate?: string) => ['gathering', gatheringId, eventDate] as const,
  gatheringRSVPs: (gatheringId: string, eventDate: string) => ['gatheringRSVPs', gatheringId, eventDate] as const,
  gatheringComments: (gatheringId: string, eventDate: string) => ['gatheringComments', gatheringId, eventDate] as const,
  groupMembers: (groupId: string) => ['groupMembers', groupId] as const,
}

/**
 * Hook for fetching upcoming gatherings
 */
export const getGatheringsQuery = (buildingUuid: string) => {
  return useQuery({
    queryKey: EVENTS_KEYS.gatherings(buildingUuid),
    queryFn: () => eventsService.getGatherings(buildingUuid),
    enabled: !!buildingUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Hook for fetching past gatherings
 */
export const getPastGatheringsQuery = (buildingUuid: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: EVENTS_KEYS.pastGatherings(buildingUuid),
    queryFn: () => eventsService.getPastGatherings(buildingUuid),
    enabled: !!buildingUuid && enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
  })
}

/**
 * Hook for checking into a gathering
 */
export const checkInMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CheckInParams) => eventsService.checkIn(params),
    onSuccess: (_, { gatheringId }) => {
      // Invalidate gatherings to refresh data
      queryClient.invalidateQueries({ queryKey: ['gatherings'] })
    },
    onError: (error) => {
      console.error('Check-in error:', error)
    },
  })
}

/**
 * Hook for updating check-in status
 */
export const updateCheckInMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: UpdateCheckInParams) => eventsService.updateCheckIn(params),
    onSuccess: () => {
      // Invalidate gatherings to refresh data
      queryClient.invalidateQueries({ queryKey: ['gatherings'] })
    },
    onError: (error) => {
      console.error('Update check-in error:', error)
    },
  })
}

/**
 * Hook for removing check-in from a gathering
 */
export const unCheckInMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ gatheringId, checkInId }: { gatheringId: string; checkInId: string }) =>
      eventsService.unCheckIn(gatheringId, checkInId),
    onSuccess: () => {
      // Invalidate gatherings to refresh data
      queryClient.invalidateQueries({ queryKey: ['gatherings'] })
    },
    onError: (error) => {
      console.error('Un-check-in error:', error)
    },
  })
}


